import { Cell } from '../palette';
import {
  crystal,
  merge,
  place,
  ringPlate,
  type MeshData,
  type Vec2,
} from './shapes';

/* ═══════════════════════════════════════════════════════════════════════════
   LE CATALOGUE — 6 meshes de base, 4 attachements, 22 silhouettes.

   ── La direction artistique, en une phrase ────────────────────────────────

   « Une flotte taillée dans la même pierre » : chaque appareil du jeu est un
   contour convexe unique, gonflé en carène à facettes, peint dans six gris-bleu
   et allumé par des accents ambre aux seuls points faibles — le joueur
   reconnaît donc un ennemi à sa SILHOUETTE et sa vulnérabilité à sa LUMIÈRE,
   sans qu'aucune couleur de la couche danger ait été dépensée.

   Pourquoi cette contrainte-là plutôt qu'une autre : sans budget d'illustration,
   la cohérence ne peut pas venir du talent de dessin, elle doit venir d'un
   INTERDIT. Celui-ci en pose trois qui se vérifient mécaniquement — une seule
   grammaire de forme (ce fichier), une seule palette (l'atlas), un seul
   matériau (`hullMaterial`) — et chacun est aujourd'hui couvert par un test.
   Le résultat n'est pas « joli parce que bien dessiné », il est cohérent parce
   qu'il n'existe aucun chemin pour ne pas l'être.

   ── Ce qui rend la capture d'écran lisible ────────────────────────────────

   1. **L'orientation porte l'allégeance.** Toute coque est dessinée proue vers
      le HAUT, puis retournée pour les hostiles. Sur une image fixe, ce qui
      pointe vers le bas veut votre mort. C'est l'information la plus rapide à
      lire de l'écran, avant même la couleur.
   2. **L'ambre ne ment jamais.** La cellule `accent` est émissive, donc elle
      franchit le seuil de bloom, donc elle attire l'œil — et elle n'est posée
      que sur des émetteurs et des points faibles. Un ennemi qui brille est un
      ennemi qui tire.
   3. **Le rouge, le rose et le violet n'existent pas ici.** Trois familles de
      projectiles se les partagent, et rien d'autre au monde n'y a droit. C'est
      ce qui rend la couche danger visible sur un fond quelconque, y compris
      au-dessus d'une explosion.

   ── Ce qui est procédural, et ce qui ne l'est pas ─────────────────────────

   Procédural : la totalité des coques, l'atlas de palette, l'environment map,
   les LUT, le décor. Zéro octet téléchargé, zéro dépendance de contenu.
   Non procédural : rien, à ce jalon. Les planches d'explosion (§4.7) restent le
   seul poste qui exigera un fichier, et elles sont hors du périmètre du J13.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── LES 6 MESHES DE BASE ──────────────────────────────────────────────────
   Contours en unités normalisées, proue vers +Y, sens trigonométrique.

   ⚠️ Tous CONVEXES. Le générateur relie chaque arête à une épine posée sur
   x = 0 ; une échancrure qui traverserait l'axe produirait des facettes
   retournées, effacées par le back-face culling — donc un trou dans la coque
   que rien ne signale. Le détail concave est le travail des attachements, qui
   sont des pièces séparées et n'ont pas cette contrainte. */

export const OUTLINES: Readonly<Record<string, readonly Vec2[]>> = {
  /** B1 — FUSEAU. Losange étroit, la forme de base du jeu : c'est elle qu'on
   *  retrouve sur le joueur, sur les drones et sur tout le popcorn. */
  spindle: [
    [0, 1],
    [-0.30, 0.16],
    [-0.22, -0.58],
    [0, -0.94],
    [0.22, -0.58],
    [0.30, 0.16],
  ],
  /** B2 — DELTA. Aile en flèche, large et basse : elle occupe de la largeur
   *  d'écran, donc elle se lit comme une menace de ZONE. */
  delta: [
    [0, 0.92],
    // ⚠️ Les extrémités d'aile sont à 0,98 du centre, pas à 1,0 tout rond : le
    // rayon d'un sommet est une DIAGONALE, et (−0,94 ; −0,42) sort du disque
    // unité de 3 %. Un contour qui déborde dessine un ennemi plus large que sa
    // hurtbox, donc des tirs qui semblent passer au travers d'une aile.
    [-0.9, -0.4],
    [-0.32, -0.7],
    [0.32, -0.7],
    [0.9, -0.4],
  ],
  /** B3 — BARRE. Coque horizontale à coins coupés : la silhouette des unités
   *  qui traversent (Rôdeur) et de celles qui posent des murs (Tisseur). */
  bar: [
    [-0.58, 0.34],
    [-1.0, 0.0],
    [-0.58, -0.34],
    [0.58, -0.34],
    [1.0, 0.0],
    [0.58, 0.34],
  ],
  /** B4 — HEXAGONE. Compact, sans direction privilégiée : la forme de ce qui
   *  encaisse et de ce qui reste en place. */
  hex: [
    [0, 1],
    [-0.866, 0.5],
    [-0.866, -0.5],
    [0, -1],
    [0.866, -0.5],
    [0.866, 0.5],
  ],
  /** B5 — CHEVRON. Pointe large : la masse qui avance, pour les lourds. */
  chevron: [
    [0, 1],
    [-0.86, -0.20],
    [-0.48, -0.86],
    [0.48, -0.86],
    [0.86, -0.20],
  ],
  /** B6 — COURONNE. Octogone aplati, réservé aux élites et aux boss : une
   *  silhouette qui n'appartient à aucune unité de troupe annonce, avant toute
   *  barre de vie, que ce qui arrive n'est pas de la même catégorie. */
  crown: [
    [0, 0.72],
    [-0.707, 0.51],
    [-1.0, 0.0],
    [-0.707, -0.51],
    [0, -0.72],
    [0.707, -0.51],
    [1.0, 0.0],
    [0.707, 0.51],
  ],
};

export type BaseName = keyof typeof OUTLINES;

/** Profondeur de carène par base. La proportion compte : une coque trop épaisse
 *  perd sa silhouette de face, une coque trop plate perd son volume. */
const RIDGE: Readonly<Record<string, [number, number]>> = {
  spindle: [0.26, 0.10],
  delta: [0.20, 0.08],
  bar: [0.24, 0.10],
  hex: [0.34, 0.14],
  chevron: [0.30, 0.12],
  crown: [0.38, 0.16],
};

function base(name: BaseName, cell: number): MeshData {
  const r = RIDGE[name] ?? [0.25, 0.1];
  return crystal(OUTLINES[name]!, { ridge: r[0]!, keel: r[1]!, cell });
}

/* ── LES 4 ATTACHEMENTS ────────────────────────────────────────────────────
   Chacun modifie la silhouette d'une manière que l'œil nomme sans effort :
   « il a des pods », « il a des ailerons », « il a un anneau », « il a un
   éperon ». C'est ce vocabulaire visuel qui permet à dix ennemis de tenir dans
   six formes sans se confondre. */

const POD_OUTLINE: readonly Vec2[] = [
  [0, 1],
  [-0.8, 0.3],
  [-0.8, -0.6],
  [0, -1],
  [0.8, -0.6],
  [0.8, 0.3],
];

const FIN_OUTLINE: readonly Vec2[] = [
  [0.1, 1],
  [-0.9, -0.25],
  [-0.55, -1],
  [0.35, -0.6],
];

const SPIKE_OUTLINE: readonly Vec2[] = [
  [0, 1],
  [-0.42, -0.55],
  [0, -1],
  [0.42, -0.55],
];

/** A1 — PODS. Deux nacelles de flanc, en ambre : elles disent « cet appareil
 *  tire », et elles sont posées exactement là où l'émetteur naît. */
function pods(spread: number, y: number, scale: number, cell: number = Cell.Accent): MeshData {
  const pod = crystal(POD_OUTLINE, { ridge: 0.5, keel: 0.2, cell });
  return merge(
    place(pod, { tx: -spread, ty: y, scale, tz: 0.02 }),
    place(pod, { tx: spread, ty: y, scale, tz: 0.02, mirrorX: true }),
  );
}

/** A2 — AILERONS. Deux dérives en flèche vers l'arrière. Elles allongent la
 *  silhouette sans l'élargir — ce qui se lit comme de la VITESSE. */
function fins(spread: number, y: number, scale: number, cell: number = Cell.Trim): MeshData {
  const fin = crystal(FIN_OUTLINE, { ridge: 0.14, keel: 0.06, cell });
  return merge(
    place(fin, { tx: -spread, ty: y, scale, rot: 0.18 }),
    place(fin, { tx: spread, ty: y, scale, rot: -0.18, mirrorX: true }),
  );
}

/** A3 — ANNEAU. Bouclier, ceinture ou collerette. C'est le seul attachement
 *  encore identifiable à douze pixels : un cercle survit à toutes les tailles,
 *  et c'est pour ça qu'il porte l'information « celui-là est protégé ». */
function ring(radius: number, cell: number = Cell.Trim): MeshData {
  return ringPlate(radius * 0.78, radius, 12, 0.06, cell);
}

/** A4 — ÉPERON. Une lame frontale. Sur un plongeur, elle annonce l'impact
 *  corporel — la seule menace du jeu qui n'émette aucun projectile et qu'il
 *  faut donc signaler par la forme seule. */
function spike(len: number, y: number, cell: number = Cell.AccentDim): MeshData {
  return place(crystal(SPIKE_OUTLINE, { ridge: 0.3, keel: 0.12, cell }), {
    ty: y,
    scaleX: len * 0.4,
    scaleY: len,
  });
}

/* ── LA TABLE DE COMPOSITION ───────────────────────────────────────────────
   Dix ennemis, sept parties destructibles, trois boss, le joueur et son drone.
   Chaque ligne est une phrase courte : une base, des attachements, une palette.

   ⚠️ Les archétypes hostiles sont RETOURNÉS (`hostile: true`) et non pas
   dessinés proue en bas. Retourner à la composition garantit qu'aucune coque ne
   puisse être saisie dans la mauvaise orientation : il n'existe qu'un seul
   endroit où le sens est décidé. */

export interface Archetype {
  readonly id: string;
  readonly build: () => MeshData;
  /** Proue vers le bas — c'est-à-dire vers le joueur. */
  readonly hostile: boolean;
  /** Contour d'inversion (`BackSide`) — réservé aux boss et aux élites
   *  (PLAN.md §4.6). Un draw call par archétype, pas par instance. */
  readonly outlined?: boolean;
}

/** Les dix du bestiaire (PLAN.md §6.4), dans l'ordre du document. */
const BESTIARY: readonly Archetype[] = [
  {
    // E01 MITE — fuseau nu. Rien à signaler : il ne tire pas, donc il ne brille
    // pas. L'absence d'ambre EST l'information.
    id: 'mite',
    hostile: true,
    build: () => base('spindle', Cell.Hull),
  },
  {
    // E02 ÉCLAT — fuseau + ailerons. Il tire une balle visée : un seul pod,
    // central, pour ne pas le confondre avec un Faucheur.
    id: 'eclat',
    hostile: true,
    build: () =>
      merge(
        base('spindle', Cell.Hull),
        fins(0.28, -0.42, 0.42),
        place(crystal(POD_OUTLINE, { ridge: 0.4, keel: 0.15, cell: Cell.Accent }), {
          ty: -0.62,
          scale: 0.17,
          tz: 0.05,
        }),
      ),
  },
  {
    // E03 DARD — fuseau + éperon. Aucun émetteur, donc aucun pod : la menace
    // est le corps lui-même, et la lame le dit.
    id: 'dard',
    hostile: true,
    build: () => merge(base('spindle', Cell.Hull), spike(0.55, 0.95, Cell.Accent)),
  },
  {
    // E04 FAUCHEUR — delta + pods. L'unité de mesure de la difficulté du jeu :
    // c'est la silhouette que le joueur verra le plus souvent, donc la plus
    // large et la plus reconnaissable des unités de troupe.
    id: 'faucheur',
    hostile: true,
    build: () => merge(base('delta', Cell.Hull), pods(0.56, -0.34, 0.2), fins(0.16, -0.5, 0.3)),
  },
  {
    // E05 RÔDEUR — barre + pods. Il traverse : la coque est horizontale, donc
    // sa direction de déplacement est lisible à l'arrêt.
    id: 'rodeur',
    hostile: true,
    build: () => merge(base('bar', Cell.Hull), pods(0.62, -0.2, 0.2), place(base('hex', Cell.HullDark), { scale: 0.34, ty: 0.06 })),
  },
  {
    // E06 MORTIER — hexagone + pods. Ancré au décor, il tire des anneaux : la
    // forme sans direction privilégiée annonce un tir sans direction.
    id: 'mortier',
    hostile: true,
    build: () =>
      merge(
        base('hex', Cell.Hull),
        place(ring(0.52, Cell.AccentDim), { tz: 0.16 }),
        pods(0.5, -0.36, 0.22),
      ),
  },
  {
    // E07 TISSEUR — barre + ailerons. Le seul ennemi qui impose une
    // trajectoire ; la coque la plus large du bestiaire, pour que le mur qu'il
    // va poser soit annoncé par sa seule envergure.
    id: 'tisseur',
    hostile: true,
    build: () =>
      merge(
        place(base('bar', Cell.Hull), { scaleX: 1.12 }),
        fins(0.78, -0.1, 0.4),
        place(base('spindle', Cell.HullDark), { scale: 0.38, ty: -0.18 }),
        place(crystal(POD_OUTLINE, { ridge: 0.4, keel: 0.15, cell: Cell.Accent }), { ty: -0.34, scale: 0.15, tz: 0.06 }),
      ),
  },
  {
    // E08 ÉGIDE — hexagone + anneau. L'anneau EST le bouclier frontal : le
    // joueur doit comprendre en un regard qu'il faut contourner, et non taper
    // plus fort.
    id: 'egide',
    hostile: true,
    build: () =>
      merge(
        base('hex', Cell.Hull),
        place(ring(0.95, Cell.Trim), { tz: 0.1 }),
        // ⚠️ Pas de cellule « player » ici, si séduisante que soit la teinte
        // pour un bouclier d'énergie : le cyan appartient au joueur et à rien
        // d'autre. Un ennemi qui l'emprunte détruit le seul repère qui permette
        // de retrouver son vaisseau au milieu de trente coques.
        place(ring(0.72, Cell.AccentDim), { tz: 0.2, ty: 0.02 }),
        pods(0.34, -0.5, 0.2),
      ),
  },
  {
    // E12 BASTION — chevron + pods + ailerons. Trois tourelles, donc trois
    // points ambre : le joueur compte ses cibles avant de tirer.
    id: 'bastion',
    hostile: true,
    build: () =>
      merge(
        base('chevron', Cell.Hull),
        pods(0.6, -0.42, 0.24),
        place(crystal(POD_OUTLINE, { ridge: 0.4, keel: 0.15, cell: Cell.Accent }), { ty: -0.72, scale: 0.2, tz: 0.06 }),
        fins(0.3, -0.2, 0.42),
        place(base('hex', Cell.HullDark), { scale: 0.3, ty: 0.18 }),
      ),
  },
  {
    // NEF — la PLATEFORME VOLANTE ennemie : une barge LARGE et plate, à deux ponts,
    // hérissée d'affûts. Silhouette horizontale maximale (plus large encore que le
    // Tisseur) pour qu'on la lise comme une STRUCTURE qui traverse, pas comme une
    // unité de troupe. L'anneau central est son aire ; les pods, ses tourelles.
    // ⚠️ PAS de contour (`outlined`) : réservé aux boss et aux élites (§4.6). La
    // nef se lit par sa LARGEUR et ses deux ponts, pas par un liseré de boss.
    id: 'nef',
    hostile: true,
    build: () =>
      merge(
        place(base('bar', Cell.HullDark), { scaleX: 1.42 }),
        place(base('bar', Cell.Hull), { scaleX: 1.16, ty: 0.05, tz: 0.06 }),
        place(ring(0.46, Cell.AccentDim), { tz: 0.12 }),
        pods(0.82, -0.02, 0.26),
        place(base('hex', Cell.HullDark), { scale: 0.24, ty: 0.02, tz: 0.14 }),
        fins(0.6, -0.24, 0.3),
      ),
  },
  {
    // E16 COLOSSE — couronne + anneau + ailerons + pods. La silhouette d'élite,
    // qui n'appartient à aucune unité de troupe.
    id: 'colosse',
    outlined: true,
    hostile: true,
    build: () =>
      merge(
        base('crown', Cell.BossHull),
        place(ring(0.62, Cell.BossTrim), { tz: 0.22 }),
        fins(0.86, -0.1, 0.5),
        pods(0.52, -0.42, 0.26),
        place(base('hex', Cell.AccentDim), { scale: 0.24, tz: 0.3 }),
      ),
  },

  /* ── Les quatre archétypes des armes ennemies exotiques ──────────────────
     Chaque silhouette dit CE QUE l'ennemi fait, avant qu'il n'ait tiré. C'est
     la règle de tout le bestiaire : sur une image fixe, la forme annonce la
     menace. Un ennemi qui lance des missiles guidés ne doit pas ressembler à
     celui qui balaie un laser. */
  {
    // HARCELEUR — lanceur de missiles guidés. Un chevron trapu hérissé de deux
    // pods : la coque d'un porte-missiles. Les pods ambre disent « ceci émet »,
    // et leur écartement annonce d'où partent les têtes chercheuses.
    id: 'harceleur',
    hostile: true,
    build: () => merge(base('chevron', Cell.Hull), pods(0.58, -0.28, 0.26), fins(0.24, -0.44, 0.3)),
  },
  {
    // TOURBILLON — anneau qui tourne. Hexagone RADIAL, sans direction
    // privilégiée, ceint d'un anneau : la seule silhouette du bestiaire qui
    // n'a pas de proue, parce que sa menace part dans toutes les directions à
    // la fois.
    id: 'tourbillon',
    hostile: true,
    build: () =>
      merge(
        base('hex', Cell.Hull),
        place(ring(0.78, Cell.Accent), { tz: 0.14 }),
        place(ring(0.5, Cell.AccentDim), { tz: 0.22 }),
      ),
  },
  {
    // SABREUR — laser vertical. Une barre longue avec un accent ambre pointé
    // VERS LE BAS : la coque annonce l'axe du faisceau. Un joueur doit pouvoir
    // deviner d'où tombera le trait avant qu'il ne s'allume.
    id: 'sabreur',
    hostile: true,
    build: () =>
      merge(
        place(base('bar', Cell.Hull), { scaleX: 0.7 }),
        place(spike(0.7, -0.9, Cell.Accent), {}),
        pods(0.4, 0.1, 0.2),
      ),
  },
  {
    // FAUCHE — zigzag serré. Un fuseau léger, presque nu : c'est un ennemi de
    // MOUVEMENT, pas d'arme. Sa silhouette maigre annonce l'agilité, pas la
    // puissance de feu.
    id: 'fauche',
    hostile: true,
    build: () => merge(base('spindle', Cell.Hull), fins(0.3, -0.2, 0.34)),
  },

  /* ── Les neuf menaces de la seconde vague d'ennemis ──────────────────────
     Kamikaze, rafale, faisceau pleine hauteur, ponte, boucliers. Chaque
     silhouette annonce la fonction : un rapace pique, un pondeur a un ventre,
     un rempart est large et frontal. */
  {
    // FAUCON — kamikaze. Un delta agressif, proue effilée par un pic vers
    // l'avant : la seule silhouette du bestiaire dont la forme dit « je fonce
    // sur toi », pas « je te tire dessus ». Le danger est le corps.
    id: 'faucon',
    hostile: true,
    build: () => merge(base('delta', Cell.Hull), spike(0.6, 0.95, Cell.Accent), fins(0.26, -0.2, 0.26)),
  },
  {
    // MITRAILLEUR — rafale de lasers. Une barre hérissée de quatre pods : autant
    // de bouches que la salve compte de traits. L'envergure annonce le mur de
    // tir bref qu'il va poser.
    id: 'mitrailleur',
    hostile: true,
    build: () =>
      merge(place(base('bar', Cell.Hull), { scaleX: 1.05 }), pods(0.66, -0.16, 0.2), pods(0.34, -0.16, 0.2)),
  },
  {
    // PRISME — faisceau vertical pleine hauteur. Un hexagone cristallin dont
    // tout l'accent pointe VERS LE BAS : la coque montre l'axe du laser avant
    // qu'il ne s'allume, pour qu'on sache quelle colonne fuir.
    id: 'prisme',
    hostile: true,
    build: () =>
      merge(
        base('hex', Cell.Hull),
        place(base('hex', Cell.HullDark), { scale: 0.5, ty: 0.1 }),
        spike(0.95, -0.95, Cell.Accent),
      ),
  },
  {
    // PONDEUSE — mère. Un corps large au VENTRE renflé (un hexagone bas ceint
    // d'un anneau) : la silhouette organique d'un pondeur, qu'on apprend à viser
    // en priorité pour couper la source.
    id: 'pondeuse',
    hostile: true,
    build: () =>
      merge(
        base('hex', Cell.Hull),
        place(base('hex', Cell.HullDark), { scale: 0.62, ty: -0.22 }),
        place(ring(0.6, Cell.AccentDim), { ty: -0.18, tz: 0.14 }),
        pods(0.4, 0.24, 0.16),
      ),
  },
  {
    // OEUF — inerte jusqu'à l'éclosion. Un petit fuseau nu, sans accent : rien
    // n'émet, rien ne menace encore. Sa neutralité EST l'information — c'est une
    // cible qu'on désamorce, pas qu'on esquive.
    id: 'oeuf',
    hostile: true,
    build: () => place(base('spindle', Cell.HullDark), { scale: 0.7 }),
  },
  {
    // LARVE — l'éclos. Un fuseau minuscule, à peine armé : la piétaille née de
    // l'œuf, dangereuse par le nombre, pas par l'unité.
    id: 'larve',
    hostile: true,
    build: () => place(base('spindle', Cell.Hull), { scale: 0.62 }),
  },
  {
    // REMPART — bouclier frontal large. La coque la plus LARGE et la plus plate
    // du bestiaire, hérissée d'ailerons frontaux : le mur qu'annonce son cône de
    // ±80°. On lit d'un regard qu'il faut passer sur le flanc.
    id: 'rempart',
    hostile: true,
    build: () =>
      merge(place(base('bar', Cell.Hull), { scaleX: 1.25 }), fins(0.86, -0.05, 0.5), pods(0.3, -0.28, 0.2)),
  },
  {
    // KYSTE — noyau sous bulle. Un hexagone compact et fermé : le corps qu'il
    // faut atteindre APRÈS avoir crevé sa membrane (dessinée par la partie
    // « bulle », séparée).
    id: 'kyste',
    hostile: true,
    build: () => merge(base('hex', Cell.Hull), place(base('hex', Cell.HullDark), { scale: 0.5, tz: 0.1 })),
  },
  {
    // BULLE — la membrane du Kyste, partie destructible à 360°. Un grand anneau
    // presque vide : le bouclier lui-même, qu'on voit s'user et céder avant que
    // le noyau ne devienne touchable.
    id: 'bulle',
    hostile: true,
    build: () => merge(place(ring(0.98, Cell.Trim), { tz: 0.12 }), place(ring(0.8, Cell.AccentDim), { tz: 0.2 })),
  },

  /* ── La famille ORGANIQUE ────────────────────────────────────────────────
     Des corps, pas des machines : formes rondes, membranes, silhouettes qui se
     répètent. Le contraste avec les coques anguleuses du reste du bestiaire est
     l'information — un banc ondule, un couveur enfle, une méduse traîne. */
  {
    // BANC — unité d'école. Petite, ronde, faite pour se répéter à l'écran : la
    // menace n'est jamais l'individu mais le mur mouvant que dessine le nombre.
    id: 'banc',
    hostile: true,
    build: () => place(base('hex', Cell.Hull), { scale: 0.6 }),
  },
  {
    // COUVEUR — incubateur dérivant. Un gros corps renflé ceint d'un anneau,
    // proche de la Pondeuse mais MOBILE : on lit la source qu'il faut couper,
    // et le ventre annonce ce qu'il sème.
    id: 'couveur',
    hostile: true,
    build: () =>
      merge(
        base('hex', Cell.Hull),
        place(base('hex', Cell.HullDark), { scale: 0.66, ty: -0.16 }),
        place(ring(0.7, Cell.AccentDim), { tz: 0.14 }),
      ),
  },
  {
    // SPORE — la feuille du Couveur, qui STAGNE. Une mote minuscule et inerte,
    // sans accent : elle n'émet rien, elle encombre. Sa neutralité dit qu'elle
    // occupe le terrain plutôt qu'elle n'attaque.
    id: 'spore',
    hostile: true,
    build: () => place(base('spindle', Cell.HullDark), { scale: 0.5 }),
  },
  {
    // MÉDUSE — corps mou traînant une grappe de dards guidés lents. Une cloche
    // (hexagone haut) prolongée d'ailerons pendants : la silhouette dit
    // « déplacement soutenu », pas « frappe ». Contrepoint du Harceleur anguleux.
    id: 'meduse',
    hostile: true,
    build: () =>
      merge(
        place(base('hex', Cell.Hull), { ty: 0.12 }),
        fins(0.5, -0.4, 0.42),
        place(ring(0.5, Cell.AccentDim), { ty: 0.16, tz: 0.12 }),
      ),
  },

  /* ── La famille BLINDÉE / forteresse ─────────────────────────────────────
     Masses lourdes et anguleuses, tourelles et casemates détachables lisibles :
     la silhouette annonce « à démonter », pas « à esquiver ». */
  {
    // CHAR — noyau verrouillé, deux canons. Un chevron trapu et large flanqué de
    // deux tourelles : on lit qu'il faut les retirer avant le corps.
    id: 'char',
    hostile: true,
    build: () =>
      merge(place(base('chevron', Cell.Hull), { scaleX: 1.1 }), pods(0.62, -0.34, 0.24), fins(0.3, -0.15, 0.34)),
  },
  { id: 'char-tourelle-g', hostile: true, build: () => merge(base('hex', Cell.Hull), pods(0.42, -0.28, 0.3)) },
  { id: 'char-tourelle-d', hostile: true, build: () => merge(base('hex', Cell.Hull), fins(0.48, -0.2, 0.42)) },
  {
    // DONJON — plateforme ancrée à trois pods. Un hexagone massif ceint d'un
    // anneau, chaque pod un pan de terrain à rouvrir.
    id: 'donjon',
    hostile: true,
    build: () =>
      merge(base('hex', Cell.Hull), place(ring(0.6, Cell.AccentDim), { tz: 0.16 }), pods(0.56, -0.3, 0.24)),
  },
  { id: 'donjon-pod-g', hostile: true, build: () => merge(base('hex', Cell.Hull), fins(0.4, -0.24, 0.36)) },
  { id: 'donjon-pod-d', hostile: true, build: () => merge(base('hex', Cell.Hull), pods(0.4, -0.24, 0.28)) },
  { id: 'donjon-pod-h', hostile: true, build: () => merge(base('hex', Cell.Hull), place(ring(0.6, Cell.AccentDim), { tz: 0.18 })) },
  {
    // PHALANGE — porteur à bouclier frontal qui protège ses voisins. Une barre
    // large, plate, hérissée d'ailerons frontaux : le mur derrière lequel
    // l'escorte s'abrite.
    id: 'phalange',
    hostile: true,
    build: () => merge(place(base('bar', Cell.Hull), { scaleX: 1.2 }), fins(0.8, -0.05, 0.48), pods(0.34, -0.26, 0.2)),
  },
  {
    // BUNKER — deux verrous empilés (casemates puis cône). Une masse compacte
    // et fermée, la plus dense du bestiaire de terrain : on la démonte, on la
    // contourne, jamais on ne l'ignore.
    id: 'bunker',
    hostile: true,
    build: () =>
      merge(
        place(base('chevron', Cell.Hull), { scaleX: 1.15 }),
        place(base('hex', Cell.HullDark), { scale: 0.5, ty: -0.1 }),
        pods(0.5, -0.36, 0.24),
      ),
  },
  { id: 'bunker-casemate-g', hostile: true, build: () => merge(base('hex', Cell.Hull), pods(0.44, -0.3, 0.28)) },
  { id: 'bunker-casemate-d', hostile: true, build: () => merge(base('hex', Cell.Hull), fins(0.5, -0.2, 0.4)) },

  /* ── La famille INTERCEPTEUR ──────────────────────────────────────────────
     Rapides, effilés, agressifs : des delta et des lames, pas des masses. La
     silhouette dit « vitesse et mouvement », jamais « puissance de feu ». */
  {
    // CHASSEUR — tireur mobile en rafale. Un delta nerveux à deux pods : il
    // balaie l'écran en crachant, la source de la salve ne tient jamais en place.
    id: 'chasseur',
    hostile: true,
    build: () => merge(base('delta', Cell.Hull), pods(0.5, -0.24, 0.2), spike(0.4, 0.9, Cell.Accent)),
  },
  {
    // GUÊPE — feinteur qui pique et remonte. Un fuseau étroit hérissé d'un dard
    // avant : le corps le plus fin du bestiaire, pour dire « je fonce et je me
    // dérobe », pas « j'encaisse ».
    id: 'guepe',
    hostile: true,
    build: () => merge(place(base('spindle', Cell.Hull), { scale: 0.9 }), spike(0.7, 0.95, Cell.Accent)),
  },
  // LAME — coupe la route à l'horizontale. Un delta couché, lame effilée : la
  // silhouette annonce un danger qui vient DE CÔTÉ, pas d'en haut.
  { id: 'lame-a', hostile: true, build: () => merge(place(base('delta', Cell.Hull), { scaleX: 1.3 }), fins(0.6, 0, 0.3)) },
  { id: 'lame-b', hostile: true, build: () => merge(place(base('delta', Cell.HullDark), { scaleX: 1.3 }), fins(0.6, 0, 0.3)) },
  // ESCADRILLE — le duo qui se croise. Deux petits chasseurs jumeaux, teintes
  // légèrement distinctes pour qu'on suive chaque branche du X.
  { id: 'escadrille-a', hostile: true, build: () => merge(place(base('delta', Cell.Hull), { scale: 0.8 }), pods(0.4, -0.2, 0.18)) },
  { id: 'escadrille-b', hostile: true, build: () => merge(place(base('delta', Cell.HullDark), { scale: 0.8 }), pods(0.4, -0.2, 0.18)) },
];

/* ── Les parties destructibles ─────────────────────────────────────────────
   Elles sont des ennemis ordinaires qui se trouvent avoir un parent, et leur
   rendu suit la même règle : une base, un attachement, la même palette. Rien
   de spécifique aux boss n'existe ici non plus. */
const PARTS: readonly Archetype[] = [
  /* Les trois tourelles du BASTION reçoivent trois attachements différents.
     C'est plus qu'une coquetterie : le plan (§6.4, E12) en fait « le meilleur
     enseignant du détruire-les-parties avant les boss », et cet apprentissage
     suppose que le joueur puisse DÉSIGNER la tourelle qu'il vise. Trois copies
     identiques ne s'apprennent pas, elles se subissent. */
  { id: 'bastion-tourelle-a', hostile: true, build: () => merge(base('hex', Cell.Hull), pods(0.44, -0.3, 0.3)) },
  { id: 'bastion-tourelle-b', hostile: true, build: () => merge(base('hex', Cell.Hull), fins(0.5, -0.2, 0.44)) },
  { id: 'bastion-tourelle-c', hostile: true, build: () => merge(base('hex', Cell.Hull), place(ring(0.62, Cell.AccentDim), { tz: 0.2 })) },

  /* Les trois sections du COLOSSE portent chacune la forme de sa fonction :
     l'aile est un delta, le dôme un hexagone couronné, le socle une barre.
     Le joueur choisit son ordre de destruction (§6.4, E16), donc il doit
     pouvoir nommer ce qu'il vise avant de l'avoir touché. */
  { id: 'colosse-aile', hostile: true, build: () => merge(base('delta', Cell.BossHull), fins(0.42, -0.3, 0.5)) },
  { id: 'colosse-dome', hostile: true, build: () => merge(base('hex', Cell.BossHull), place(ring(0.7, Cell.BossTrim), { tz: 0.24 }), place(base('hex', Cell.AccentDim), { scale: 0.3, tz: 0.36 })) },
  { id: 'colosse-socle', hostile: true, build: () => merge(base('bar', Cell.BossHull), pods(0.62, -0.2, 0.24)) },

  { id: 'heraut-canon', hostile: true, build: () => merge(base('hex', Cell.BossHull), spike(0.5, 0.9, Cell.Accent), place(ring(0.55, Cell.BossTrim), { tz: 0.18 })) },
  { id: 'heraut-bras', hostile: true, build: () => merge(base('bar', Cell.BossHull), fins(0.66, -0.1, 0.42), pods(0.3, -0.28, 0.22)) },
  { id: 'heraut-nacelle', hostile: true, build: () => merge(base('spindle', Cell.BossHull), pods(0.34, -0.3, 0.22)) },
  { id: 'navette-fuseau', hostile: true, build: () => merge(base('spindle', Cell.BossHull), fins(0.3, -0.4, 0.44)) },
  { id: 'navette-peigne', hostile: true, build: () => merge(base('bar', Cell.BossHull), pods(0.7, -0.14, 0.2), pods(0.24, -0.14, 0.2)) },
];

/* ── Les 3 boss modulaires ─────────────────────────────────────────────────
   « Modulaire » veut dire ici : assemblés depuis le MÊME kit que la troupe, à
   une autre échelle. C'est ce qui empêche un boss de ressembler à un objet
   importé d'un autre jeu — le défaut le plus courant quand les boss sont
   modélisés à part. */
const BOSSES: readonly Archetype[] = [
  {
    // B01 LE HÉRAUT — la nef. Large, symétrique, frontale : le boss qui
    // enseigne doit être lisible comme un schéma.
    id: 'heraut',
    outlined: true,
    hostile: true,
    build: () =>
      merge(
        place(base('crown', Cell.BossHull), { scaleX: 1.2 }),
        place(base('bar', Cell.BossTrim), { scale: 0.72, ty: -0.24 }),
        place(ring(0.44, Cell.AccentDim), { tz: 0.3, ty: 0.05 }),
        pods(0.86, -0.3, 0.3),
        fins(1.05, 0.05, 0.5),
      ),
  },
  {
    // B02 LA TISSERANDE — la rosace. Six bras à 60°, exactement la structure de
    // ses spirales déphasées : la forme du boss ANNONCE son pattern, et c'est
    // le seul indice dont le joueur dispose avant la première salve.
    id: 'navette',
    outlined: true,
    hostile: true,
    build: () => {
      const arms: MeshData[] = [];
      for (let i = 0; i < 6; i++) {
        const a = (i * Math.PI * 2) / 6;
        arms.push(
          place(crystal(FIN_OUTLINE, { ridge: 0.14, keel: 0.06, cell: Cell.BossTrim }), {
            tx: Math.sin(a) * 0.66,
            ty: Math.cos(a) * 0.66,
            rot: -a,
            scale: 0.46,
          }),
        );
      }
      return merge(
        base('hex', Cell.BossHull),
        ...arms,
        place(ring(0.9, Cell.BossTrim), { tz: 0.05 }),
        place(ring(0.34, Cell.Accent), { tz: 0.34 }),
      );
    },
  },
  {
    // B03 LA FORGE — la masse industrielle. La seule à bouger en Z ; sa carène
    // est donc la plus épaisse du jeu, pour que le déplacement se VOIE.
    id: 'colosse-boss',
    outlined: true,
    hostile: true,
    build: () =>
      merge(
        place(base('crown', Cell.BossHull), { scaleX: 1.15, scaleY: 1.25 }),
        place(base('chevron', Cell.BossTrim), { scale: 0.62, ty: -0.3 }),
        place(ring(0.55, Cell.AccentDim), { tz: 0.36 }),
        pods(0.94, -0.2, 0.32),
        pods(0.62, 0.42, 0.24),
        spike(0.7, 1.05, Cell.Accent),
      ),
  },
];

/* ── Le joueur et son escorte ──────────────────────────────────────────────
   Proue vers le HAUT, cellules cyan. Le joueur ne partage aucune couleur avec
   la flotte hostile — ni l'ambre des accents, ni les gris de coque — ce qui
   permet de le retrouver instantanément au milieu de trente ennemis. */
const PLAYER: readonly Archetype[] = [
  {
    id: 'player',
    hostile: false,
    build: () =>
      merge(
        place(base('spindle', Cell.PlayerHull), { scaleY: 1.15 }),
        fins(0.3, -0.44, 0.46, Cell.PlayerTrim),
        place(base('delta', Cell.PlayerTrim), { scale: 0.44, ty: -0.16 }),
        // Les tuyères : pleinement émissives, donc elles bloomeront. C'est
        // l'ancre visuelle du vaisseau et elle est derrière lui, jamais devant
        // le point de hitbox.
        place(crystal(POD_OUTLINE, { ridge: 0.4, keel: 0.15, cell: Cell.PlayerGlow }), { tx: -0.2, ty: -0.76, scale: 0.14 }),
        place(crystal(POD_OUTLINE, { ridge: 0.4, keel: 0.15, cell: Cell.PlayerGlow }), { tx: 0.2, ty: -0.76, scale: 0.14 }),
      ),
  },
  {
    id: 'drone',
    hostile: false,
    build: () =>
      merge(
        base('spindle', Cell.DroneHull),
        place(crystal(POD_OUTLINE, { ridge: 0.4, keel: 0.15, cell: Cell.DroneGlow }), { ty: -0.5, scale: 0.3 }),
      ),
  },

  /* ── LES SILHOUETTES DE VAISSEAU ───────────────────────────────────────────
     Un archétype par vaisseau achetable (`ship-<id>`), pour que le HANGAR et la
     partie montrent une coque qui correspond à ce qu'on pilote — et pas la même
     silhouette repeinte. Chacune emprunte au vocabulaire d'attachements existant
     ce qui dit son IDENTITÉ d'un coup d'œil, exactement comme les ennemis :

       · LANCE  → coque longue et fine + ÉPERON frontal : la perforation ;
       · AEGIS  → coque large et trapue + ANNEAU : le blindage, le bouclier ;
       · FANAL  → cœur incandescent + grosses tuyères : le phare qui brille.

     ⚠️ Le VULCAN n'a pas d'archétype dédié : il EST la silhouette « player » de
     série. `View.setShip` retombe dessus pour tout identifiant sans coque propre,
     si bien qu'un futur vaisseau reste visible (jamais un objet manquant) tant
     qu'on ne lui a pas taillé la sienne. */
  {
    id: 'ship-lance',
    hostile: false,
    build: () =>
      merge(
        // Longue et étroite : la vitesse et la perforation se lisent avant le tir.
        place(base('spindle', Cell.PlayerHull), { scaleY: 1.55, scaleX: 0.7 }),
        // L'éperon — littéralement la lance, à la proue.
        spike(0.7, 0.92, Cell.PlayerTrim),
        // Ailerons courts et très en flèche.
        fins(0.16, -0.56, 0.32, Cell.PlayerTrim),
        place(crystal(POD_OUTLINE, { ridge: 0.4, keel: 0.15, cell: Cell.PlayerGlow }), { ty: -0.98, scale: 0.13 }),
      ),
  },
  {
    id: 'ship-aegis',
    hostile: false,
    build: () =>
      merge(
        // Large et trapue : la masse et le blindage.
        place(base('hex', Cell.PlayerHull), { scaleX: 1.15, scaleY: 0.95 }),
        // L'anneau — le bouclier de l'AEGIS, reconnaissable à toute taille.
        ring(0.98, Cell.PlayerTrim),
        // Ailes larges et basses.
        fins(0.52, -0.28, 0.42, Cell.PlayerTrim),
        place(crystal(POD_OUTLINE, { ridge: 0.4, keel: 0.15, cell: Cell.PlayerGlow }), { tx: -0.3, ty: -0.68, scale: 0.16 }),
        place(crystal(POD_OUTLINE, { ridge: 0.4, keel: 0.15, cell: Cell.PlayerGlow }), { tx: 0.3, ty: -0.68, scale: 0.16 }),
      ),
  },
  {
    id: 'ship-fanal',
    hostile: false,
    build: () =>
      merge(
        place(base('chevron', Cell.PlayerHull), { scale: 1.0 }),
        // Le cœur incandescent — le « fanal », un phare porté par le vaisseau.
        place(base('crown', Cell.PlayerGlow), { scale: 0.46, ty: 0.06 }),
        fins(0.34, -0.4, 0.4, Cell.PlayerTrim),
        // De grosses tuyères éclatantes : cet appareil brille plus que les autres.
        place(crystal(POD_OUTLINE, { ridge: 0.4, keel: 0.15, cell: Cell.PlayerGlow }), { tx: -0.22, ty: -0.66, scale: 0.19 }),
        place(crystal(POD_OUTLINE, { ridge: 0.4, keel: 0.15, cell: Cell.PlayerGlow }), { tx: 0.22, ty: -0.66, scale: 0.19 }),
      ),
  },
];

export const ARCHETYPES: readonly Archetype[] = [...BESTIARY, ...PARTS, ...BOSSES, ...PLAYER];

/** Indice d'un archétype par identifiant. Construit une fois. */
export const ARCHETYPE_INDEX: ReadonlyMap<string, number> = new Map(
  ARCHETYPES.map((a, i) => [a.id, i]),
);

/** Archétype de repli, par RANG (PLAN.md §6.4 : popcorn / troupe / lourd).
 *
 *  ⚠️ Il n'existe pas par élégance mais par nécessité opérationnelle : le
 *  contenu vit dans `src/content/`, il est modifié par d'autres chantiers, et
 *  un ennemi ajouté là-bas ne doit JAMAIS apparaître comme un objet manquant.
 *  Un ennemi invisible reste mortel — c'est la pire panne possible dans un
 *  shmup. Il hérite donc de la silhouette de sa catégorie, ce qui est faux mais
 *  jouable, et le test de couverture signale l'oubli en CI. */
const FALLBACK_BY_RANK = ['mite', 'faucheur', 'bastion'];

export function archetypeFor(id: string, rank: number): number {
  const direct = ARCHETYPE_INDEX.get(id);
  if (direct !== undefined) return direct;
  const fb = FALLBACK_BY_RANK[Math.max(0, Math.min(rank, FALLBACK_BY_RANK.length - 1))]!;
  return ARCHETYPE_INDEX.get(fb) ?? 0;
}

/** Construit toutes les géométries. Appelé UNE fois au démarrage. */
export function buildAllMeshes(): MeshData[] {
  return ARCHETYPES.map((a) => {
    const m = a.build();
    /* Le retournement des hostiles est fait ICI et nulle part ailleurs.
       Une ROTATION d'un demi-tour, pas deux échelles négatives : le résultat
       géométrique serait identique, mais la rotation ne pose aucune question
       d'orientation des faces et se lit sans avoir à y réfléchir. */
    return a.hostile ? place(m, { rot: Math.PI }) : m;
  });
}
